import React, { useState } from 'react';
import { X, Upload, FileSpreadsheet, AlertCircle, CheckCircle } from 'lucide-react';
import { itemsAPI } from "../../services/api";
import { toast } from "react-toastify";

const BulkUploadModal = ({ onClose, onUpload }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  React.useEffect(() => {
    // Trigger animation on mount
    setIsOpen(true);
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.xlsx')) {
        toast.error('Please select a valid Excel file (.xlsx)');
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size should be less than 10MB');
        return;
      }
      
      setSelectedFile(file);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await itemsAPI.bulkUpload(formData);
      
      const successCount = response.data.count || 0;
      setUploadResult({
        success: true,
        count: successCount,
        items: response.data.items || []
      });
      
      toast.success(`Successfully uploaded ${successCount} items`);
      
      // Auto-close after 2 seconds on success
      setTimeout(() => {
        onUpload();
      }, 2000);
      
    } catch (error) {
      console.error('Error uploading file:', error);
      const errorMessage = error.response?.data?.error || 'Failed to upload file';
      
      setUploadResult({
        success: false,
        error: errorMessage
      });
      
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(onClose, 300); // Wait for animation to complete
  };

  const removeFile = () => {
    setSelectedFile(null);
    setUploadResult(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end z-50">
      <div 
        className={`bg-white h-full w-full max-w-2xl shadow-xl flex flex-col transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Bulk Upload Items</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={uploading}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-blue-900 mb-2">Instructions:</h3>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>Download the Excel template using the "Template" button on the main page</li>
              <li>Fill in the item details (Item Name, HSN/SAC Code, Description)</li>
              <li>Save the file and upload it here</li>
              <li>Note: Images cannot be uploaded via Excel. Add images individually after upload</li>
            </ol>
          </div>

          {/* File Upload Area */}
          {!selectedFile ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
              <input
                type="file"
                id="bulkUploadFile"
                accept=".xlsx"
                onChange={handleFileSelect}
                className="hidden"
                disabled={uploading}
              />
              <label
                htmlFor="bulkUploadFile"
                className="cursor-pointer flex flex-col items-center"
              >
                <FileSpreadsheet size={64} className="text-gray-400 mb-4" />
                <span className="text-lg text-gray-700 mb-2 font-medium">
                  Click to select Excel file
                </span>
                <span className="text-sm text-gray-500">
                  Only .xlsx files are supported (max 10MB)
                </span>
              </label>
            </div>
          ) : (
            <div className="border-2 border-blue-500 rounded-lg p-6 bg-blue-50">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <FileSpreadsheet size={40} className="text-blue-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">{selectedFile.name}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                </div>
                {!uploading && !uploadResult?.success && (
                  <button
                    onClick={removeFile}
                    className="text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Upload Result */}
          {uploadResult && (
            <div className={`mt-4 p-4 rounded-lg border ${
              uploadResult.success 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start gap-3">
                {uploadResult.success ? (
                  <CheckCircle size={24} className="text-green-600 flex-shrink-0" />
                ) : (
                  <AlertCircle size={24} className="text-red-600 flex-shrink-0" />
                )}
                <div>
                  <p className={`font-medium ${
                    uploadResult.success ? 'text-green-900' : 'text-red-900'
                  }`}>
                    {uploadResult.success 
                      ? `Successfully uploaded ${uploadResult.count} items` 
                      : 'Upload failed'}
                  </p>
                  {uploadResult.error && (
                    <p className="text-sm text-red-700 mt-1">{uploadResult.error}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t flex-shrink-0">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={uploading}
          >
            {uploadResult?.success ? 'Close' : 'Cancel'}
          </button>
          {!uploadResult?.success && (
            <button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-400 flex items-center gap-2"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Upload Items
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;